import { useState, useEffect, useCallback, useRef } from 'react';
import { scoresheetService } from '../services/scoresheetService';
import { calculatedStatsService } from '../services/calculatedStatsService';
import { UIMatchStats, UIScoresheetStat, StatType } from '../types/database';
import { useAuth } from '../contexts/AuthContext';
import { useMatchTimeline } from './useMatchTimeline';
import { supabase } from '../lib/supabase';

export const useScoresheet = (matchId: string | undefined, homeTeamId: string | undefined, awayTeamId: string | undefined) => {
  const { user } = useAuth();
  
  // Match timeline integration
  const { matchStatus, isLoading: timelineLoading } = useMatchTimeline(matchId || '');
  
  // Core state
  const [matchStats, setMatchStats] = useState<UIMatchStats | null>(null);
  const [statTypes, setStatTypes] = useState<StatType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // UI state
  const [possession, setPossession] = useState<'home' | 'away'>('home');
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [pendingUpdates, setPendingUpdates] = useState<Array<{
    teamId: string;
    statKey: string;
    action: 'increment' | 'decrement';
  }>>([]);

  // Realtime subscription ref
  const subscriptionRef = useRef<any>(null);

  // Initialize stat type cache and fetch data
  const fetchScoresheetData = useCallback(async () => {
    console.log('useScoresheet - fetchScoresheetData called with:', {
      matchId,
      homeTeamId,
      awayTeamId,
      userId: user?.id
    });
    
    if (!matchId || !homeTeamId || !awayTeamId) {
      console.log('useScoresheet - Missing required data, skipping fetch');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Initialize stat type cache first
      await scoresheetService.initializeStatTypeCache();
      
      // Fetch stat types for UI rendering
      const statTypesData = await scoresheetService.getStatTypes();
      setStatTypes(statTypesData);
      
      // Fetch match stats
      const statsData = await scoresheetService.getMatchStats(matchId);
      setMatchStats(statsData);
      
      console.log('useScoresheet - Data fetched successfully:', {
        statTypesCount: statTypesData.length,
        homeStatsCount: statsData.home_team_stats.length,
        awayStatsCount: statsData.away_team_stats.length
      });
      
    } catch (err) {
      setError('Error fetching scoresheet data');
      console.error('Error in useScoresheet:', err);
    } finally {
      setLoading(false);
    }
  }, [matchId, homeTeamId, awayTeamId, user?.id]);

  // Update a single stat (optimistic update)
  const updateStat = useCallback(async (
    teamId: string, 
    statKey: string, 
    action: 'increment' | 'decrement',
    playerId?: string,
    positionData?: any,
    videoTimeSeconds?: number | null,
    videoId?: string | null,
    manualTags?: string[]
  ) => {
    console.log(`useScoresheet - updateStat called: ${statKey} for team ${teamId}, action: ${action}`);
    
    if (!matchId) {
      console.error('useScoresheet - Cannot update stat: missing matchId');
      return;
    }

    // Check if match is live (skip validation for game_flow stats)
    // Only block if we're absolutely certain the match hasn't started
    // If status is uncertain (loading, stale, or missing), let the service-level check handle it
    if (!['kick_off', 'half_time', 'full_time', 'substitution_in', 'substitution_out'].includes(statKey)) {
      // Clear any previous errors first
      setError(null);
      
      // Only block if:
      // 1. Status is definitely loaded (not loading)
      // 2. Status is definitely not_started
      // 3. matchId exists
      // Otherwise, let the service query the database to get accurate status
      if (timelineLoading === false && matchStatus.status === 'not_started' && matchId) {
        // Double-check: if matchId exists but status is not_started, it might be stale
        // Let the service handle it to be safe
        // Don't block here - let service query and throw if truly not started
      }
      // Only block if status is definitely full_time and we're not loading
      if (timelineLoading === false && matchStatus.status === 'full_time' && matchId) {
        setError('Cannot update stats - match has ended');
        return;
      }
      // If status is loading, uncertain, or matchId is missing, let service handle validation
    } else {
      // Clear error for game flow stats
      setError(null);
    }

    // Optimistic update - update UI immediately
    setMatchStats(prevStats => {
      if (!prevStats) {
        // If no stats exist yet, create a minimal structure for immediate UI update
        const homeStats: UIScoresheetStat[] = [];
        const awayStats: UIScoresheetStat[] = [];
        
        // Create a temporary stat entry for immediate UI response
        const tempStat: UIScoresheetStat = {
          stat_type: {
            id: statKey,
            category: 'defense',
            stat_key: statKey,
            display_name: statKey,
            short_description: '',
            event_result: 'neutral',
            is_active: true,
            is_calculated: false,
            visible_in_reports: null,
            top_stats: null,
            sort_order: 0,
            created_at: new Date().toISOString()
          },
          value: action === 'increment' ? 1 : 0,
          team_id: teamId
        };

        if (teamId === homeTeamId) {
          homeStats.push(tempStat);
        } else {
          awayStats.push(tempStat);
        }

        return {
          match_id: matchId,
          home_team_stats: homeStats,
          away_team_stats: awayStats
        };
      }
      
      const teamStats = teamId === homeTeamId ? prevStats.home_team_stats : prevStats.away_team_stats;
      
      // Check if the stat already exists
      const existingStat = teamStats.find(stat => stat.stat_type.stat_key === statKey);
      
      
      let updatedTeamStats: UIScoresheetStat[];
      
      if (existingStat) {
        // Stat exists - update it
        updatedTeamStats = teamStats.map(stat => {
          if (stat.stat_type.stat_key === statKey) {
            const newValue = Math.max(0, stat.value + (action === 'increment' ? 1 : -1));
            return { ...stat, value: newValue };
          }
          return stat;
        });
      } else {
        // Stat doesn't exist - create new one
        const newValue = action === 'increment' ? 1 : 0;
        
        const newStat: UIScoresheetStat = {
          stat_type: {
            id: statKey,
            category: 'defense', // Will be overridden by frontend stats
            stat_key: statKey,
            display_name: statKey,
            short_description: '',
            event_result: 'neutral',
            is_active: true,
            is_calculated: false,
            visible_in_reports: null,
            top_stats: null,
            sort_order: 0,
            created_at: new Date().toISOString()
          },
          value: newValue,
          team_id: teamId
        };
        
        updatedTeamStats = [...teamStats, newStat];
      }

      return {
        ...prevStats,
        [teamId === homeTeamId ? 'home_team_stats' : 'away_team_stats']: updatedTeamStats
      };
    });

    // Add to pending updates for batch save
    setPendingUpdates(prev => [...prev, { teamId, statKey, action }]);
    setUnsavedChanges(true);

    // Calculate isLive from cached matchStatus (performance optimization)
    // If status is uncertain (loading or not_started but might be stale), pass undefined to let service query
    const isLive = timelineLoading || matchStatus.status === 'not_started'
      ? undefined // Let service query if status is uncertain
      : (matchStatus.status === 'live' || 
         matchStatus.status === 'half_time' || 
         matchStatus.status === 'editing' ||
         matchStatus.status === 'extra_time_1st_half' || 
         matchStatus.status === 'extra_time_half_time' || 
         matchStatus.status === 'extra_time_2nd_half' || 
         matchStatus.status === 'penalty_shootout');

    // Background sync with database (realtime will handle UI updates)
    try {
      await scoresheetService.updateStat(
        matchId, 
        teamId, 
        statKey, 
        action, 
        user?.id, 
        undefined, 
        playerId, 
        positionData,
        videoTimeSeconds ?? undefined,
        videoId ?? undefined,
        manualTags,
        isLive // Pass cached isLive value instead of querying
      );
      
      // Remove from pending updates on success
      setPendingUpdates(prev => prev.filter(update => 
        !(update.teamId === teamId && update.statKey === statKey && update.action === action)
      ));
      
      console.log(`useScoresheet - Stat updated successfully: ${statKey}`);
    } catch (err) {
      console.error('useScoresheet - Error updating stat:', err);
      // Revert optimistic update on error
      setMatchStats(prevStats => {
        if (!prevStats) return prevStats;
        
        const teamStats = teamId === homeTeamId ? prevStats.home_team_stats : prevStats.away_team_stats;
        const updatedTeamStats = teamStats.map(stat => {
          if (stat.stat_type.stat_key === statKey) {
            const revertedValue = Math.max(0, stat.value - (action === 'increment' ? 1 : -1));
            return { ...stat, value: revertedValue };
          }
          return stat;
        });
        
        return {
          ...prevStats,
          [teamId === homeTeamId ? 'home_team_stats' : 'away_team_stats']: updatedTeamStats
        };
      });
      
      // Keep the update in pendingUpdates for retry
    }
  }, [matchId, homeTeamId, matchStats, matchStatus, timelineLoading, user?.id]);

  // Batch update multiple stats
  const batchUpdateStats = useCallback(async (
    updates: Array<{ teamId: string; statKey: string; action: 'increment' | 'decrement' }>
  ) => {
    console.log(`useScoresheet - batchUpdateStats called with ${updates.length} updates`);
    
    if (!matchId) {
      console.error('useScoresheet - Cannot batch update: missing matchId');
      return;
    }

    // Group updates by team
    const updatesByTeam = updates.reduce((acc, update) => {
      if (!acc[update.teamId]) {
        acc[update.teamId] = [];
      }
      acc[update.teamId].push({ statKey: update.statKey, action: update.action });
      return acc;
    }, {} as Record<string, Array<{ statKey: string; action: 'increment' | 'decrement' }>>);

    // Process each team's updates
    const promises = Object.entries(updatesByTeam).map(([teamId, teamUpdates]) =>
      scoresheetService.batchUpdateStats(matchId, teamId, teamUpdates, user?.id)
    );

    try {
      await Promise.all(promises);
      
      // Clear pending updates on success
      setPendingUpdates([]);
      setUnsavedChanges(false);
      
      console.log('useScoresheet - Batch update completed successfully');
    } catch (err) {
      console.error('useScoresheet - Error in batch update:', err);
      throw err;
    }
  }, [matchId, user?.id]);

  // Save all pending changes
  // Note: Stats calculations (match stats and player stats) have been moved to Config tab
  // This function now only saves pending stat updates to scoresheet_logs
  const saveChanges = useCallback(async () => {
    console.log(`useScoresheet - saveChanges called with ${pendingUpdates.length} pending updates`);
    
    try {
      // Save pending updates if any
      if (pendingUpdates.length > 0) {
        await batchUpdateStats(pendingUpdates);
        console.log('useScoresheet - All changes saved successfully');
      } else {
        console.log('useScoresheet - No pending changes to save');
      }
      
      // Stats calculations (match stats and player stats) are now handled in Config tab
      // Use Config tab to compute statistics after saving changes
    } catch (err) {
      console.error('useScoresheet - Error saving changes:', err);
      throw err;
    }
  }, [pendingUpdates, batchUpdateStats]);

  // Refresh calculated stats (for reports app)
  const refreshCalculatedStats = useCallback(async () => {
    if (!matchId || !homeTeamId || !awayTeamId) {
      console.error('useScoresheet - Cannot refresh calculated stats: missing IDs');
      return false;
    }

    try {
      console.log('useScoresheet - Refreshing calculated stats...');
      await calculatedStatsService.calculateAndSaveAllStats(matchId, homeTeamId, awayTeamId);
      console.log('useScoresheet - Calculated stats refreshed successfully');
      
      // Refetch match stats to show updated calculated stats
      await fetchScoresheetData();
      
      return true;
    } catch (err) {
      console.error('useScoresheet - Error refreshing calculated stats:', err);
      return false;
    }
  }, [matchId, homeTeamId, awayTeamId]);

  // Get stats for a specific team
  const getTeamStats = useCallback((teamId: string): UIScoresheetStat[] => {
    if (!matchStats) return []; // Return empty array if no match stats
    
    return teamId === homeTeamId ? matchStats.home_team_stats : matchStats.away_team_stats;
  }, [matchStats, homeTeamId]);

  // Get stats by category for a team
  const getTeamStatsByCategory = useCallback((teamId: string, category: string): UIScoresheetStat[] => {
    const teamStats = getTeamStats(teamId);
    return teamStats.filter(stat => stat.stat_type.category === category);
  }, [getTeamStats]);

  // Get stat value for a specific stat key and team
  const getStatValue = useCallback((teamId: string, statKey: string): number => {
    const teamStats = getTeamStats(teamId);
    const stat = teamStats.find(s => s.stat_type.stat_key === statKey);
    return stat?.value || 0; // Always return 0 if stat not found in DB
  }, [getTeamStats]);

  // Switch possession
  const switchPossession = useCallback(() => {
    setPossession(prev => prev === 'home' ? 'away' : 'home');
  }, []);

  // Set possession
  const setPossessionTeam = useCallback((team: 'home' | 'away') => {
    setPossession(team);
  }, []);

  // Initialize scoresheet for a new match
  const initializeScoresheet = useCallback(async () => {
    console.log('useScoresheet - initializeScoresheet called');
    
    if (!matchId || !homeTeamId || !awayTeamId) {
      console.error('useScoresheet - Cannot initialize: missing required IDs');
      return;
    }

    try {
      await scoresheetService.initializeMatchScoresheet(matchId, homeTeamId, awayTeamId);
      
      // Refresh data after initialization
      await fetchScoresheetData();
      
      console.log('useScoresheet - Scoresheet initialized successfully');
    } catch (err) {
      console.error('useScoresheet - Error initializing scoresheet:', err);
      throw err;
    }
  }, [matchId, homeTeamId, awayTeamId, fetchScoresheetData]);

  // Clear all stats for a match
  const clearAllStats = useCallback(async () => {
    console.log('useScoresheet - clearAllStats called');
    
    if (!matchId) {
      console.error('useScoresheet - Cannot clear stats: missing matchId');
      return;
    }

    try {
      await scoresheetService.clearMatchStats(matchId);
      
      // Refresh data after clearing
      await fetchScoresheetData();
      
      // Clear UI state
      setPendingUpdates([]);
      setUnsavedChanges(false);
      
      console.log('useScoresheet - All stats cleared successfully');
    } catch (err) {
      console.error('useScoresheet - Error clearing stats:', err);
      throw err;
    }
  }, [matchId, fetchScoresheetData]);

  // Fetch data on mount and when dependencies change
  useEffect(() => {
    fetchScoresheetData();
  }, [fetchScoresheetData]);
  
  // Refresh scoresheet when match status changes
  useEffect(() => {
    if (matchStatus && !timelineLoading) {
      console.log('Match status changed, refreshing scoresheet data:', matchStatus);
      fetchScoresheetData();
    }
  }, [matchStatus.status, fetchScoresheetData]);

  // Setup realtime subscription for scoresheet updates
  useEffect(() => {
    if (!matchId) return;

    console.log('🔴 Setting up realtime subscription for match:', matchId);

    // Clean up existing subscription
    if (subscriptionRef.current) {
      console.log('🔴 Cleaning up existing subscription');
      supabase.removeChannel(subscriptionRef.current);
    }

    // Create new subscription
    const subscription = supabase
      .channel(`scoresheet-${matchId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'scoresheet',
          filter: `match_id=eq.${matchId}`
        },
        (payload) => {
          console.log('🔴 Realtime scoresheet update received:', payload);
          
          // Update local state optimistically
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            setMatchStats(prevStats => {
              if (!prevStats) return prevStats;
              
              const newStats = { ...prevStats };
              const updatedEntry = payload.new;
              
              // Find and update the specific stat
              const homeIndex = newStats.home_team_stats.findIndex(
                stat => stat.stat_type.id === updatedEntry.stat_type_id && stat.team_id === updatedEntry.team_id
              );
              const awayIndex = newStats.away_team_stats.findIndex(
                stat => stat.stat_type.id === updatedEntry.stat_type_id && stat.team_id === updatedEntry.team_id
              );
              
              if (homeIndex !== -1) {
                newStats.home_team_stats[homeIndex] = {
                  ...newStats.home_team_stats[homeIndex],
                  value: updatedEntry.value
                };
              } else if (awayIndex !== -1) {
                newStats.away_team_stats[awayIndex] = {
                  ...newStats.away_team_stats[awayIndex],
                  value: updatedEntry.value
                };
              }
              
              return newStats;
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'scoresheet_logs',
          filter: `match_id=eq.${matchId}`
        },
        (payload) => {
          console.log('🔴 Realtime scoresheet log update received:', payload);
          // We can use this for audit trail updates if needed
        }
      )
      .subscribe((status) => {
        console.log('🔴 Subscription status:', status);
      });

    subscriptionRef.current = subscription;

    // Cleanup function
    return () => {
      console.log('🔴 Cleaning up realtime subscription');
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
      }
    };
  }, [matchId]);

  return {
    // Core data
    matchStats,
    statTypes,
    loading: loading || timelineLoading,
    error,
    
    // Match status
    matchStatus,
    
    // UI state
    possession,
    unsavedChanges,
    pendingUpdatesCount: pendingUpdates.length,
    
    // Actions
    updateStat,
    batchUpdateStats,
    saveChanges,
    refreshCalculatedStats,
    switchPossession,
    setPossessionTeam,
    initializeScoresheet,
    clearAllStats,
    
    // Getters
    getTeamStats,
    getTeamStatsByCategory,
    getStatValue,
    
    // Utilities
    refetch: fetchScoresheetData
  };
};
